import React, { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { TextField } from '@one-thd/sui-atomic-components';
import { useDataModel } from '@thd-nucleus/data-sources';
import { TZipCodeFormValues } from '../../../models/common/zipcode';
import { getTextFieldStatus } from '../../../utils/forms';
import { ZIPCODE_LENGTH } from '../../../constants/general';
import { CityStateSelect } from '../city-state-select/city-state-select';
import { ADDRESS_FORM_DATA_TEST_IDS } from '../../../constants/address-constants';
import { TCity } from '../../../models/common/city-state';
import { cityStateByZipCode } from '../../../models/graphQLModels';
import { getIsAuthError } from '../../../constants/error-codes';
import { TAlertStatus, TAlertType } from '../../../models/common/alert';
import { ERR_REDIRECT } from '../../../constants/response-error-messages';
import { redirectToSignIn } from '../../../utils/general';
import { useInstantCheckoutStatus } from '../../../hooks/use-instant-checkout-status';
import {
  createNewRelicActions,
  logNewRelicAction,
} from '../../../utils/newrelic';
import { useViews } from '../../../hooks/use-views';

interface IZipCodeProps {
  disabled?: boolean;
}

export const ZipCodeCitySelectFields = ({ disabled }: IZipCodeProps) => {
  const {
    control,
    getValues,
    setValue,
    formState: { errors: formErrors },
  } = useFormContext<TZipCodeFormValues>();

  const { currentView } = useViews();
  const actions = createNewRelicActions(currentView);

  const { setAlertMessage } = useInstantCheckoutStatus();

  const normalizeCityState = getValues('cityState');
  const defaultCities = normalizeCityState
    ? [
        {
          city: normalizeCityState.split(',')[0].trim(),
          stateProvince: normalizeCityState.split(',')[1].trim(),
        },
      ]
    : null;

  const [zipCodeValue, setZipCodeValue] = React.useState('');
  const [cities, setCities] = React.useState<TCity[] | null>(defaultCities);

  const {
    data: locations,
    refetch: locationsRef,
    error,
  } = useDataModel('cityStateByZipCode', {
    variables: { zipCode: zipCodeValue },
    skip: zipCodeValue?.length !== 5,
    ssr: false,
    context: { withAuth: true },
  });

  useEffect(() => {
    if (error) {
      logNewRelicAction(actions.ZIPCODE, error);
      if (getIsAuthError(error)) {
        setAlertMessage({
          alertType: TAlertType.GENERAL,
          alertStatus: TAlertStatus.INFO,
          text: ERR_REDIRECT.message,
        });
        redirectToSignIn();
      }
    }
  }, [error]);

  useEffect(() => {
    if (zipCodeValue?.length === 5) {
      locationsRef();
    }
  }, [zipCodeValue, locationsRef]);

  useEffect(() => {
    if (
      locations?.cityStateByZipCode &&
      Array.isArray(locations.cityStateByZipCode)
    ) {
      logNewRelicAction(actions.ZIPCODE);
      setCities(locations.cityStateByZipCode);
      setValue(
        'cityState',
        locations.cityStateByZipCode[0].city +
          ', ' +
          locations.cityStateByZipCode[0].stateProvince,
      );
    }
  }, [locations, setValue]);

  const handleZipCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newZipCode = event.currentTarget.value;
    setZipCodeValue(newZipCode);
  };

  return (
    <>
      <Controller
        name="zipCode"
        control={control}
        disabled={disabled}
        render={({ field }) => {
          const { ref, value, name, onChange, ...rest } = field;

          const onChangeRevised = (
            event: React.ChangeEvent<HTMLInputElement>,
          ) => {
            handleZipCodeChange(event);
            onChange(event);
          };

          return (
            <TextField
              label="ZIP Code*"
              data-testid={ADDRESS_FORM_DATA_TEST_IDS.zipCodeTextField}
              status={getTextFieldStatus(formErrors.zipCode)}
              statusMessage={formErrors.zipCode?.message}
              value={value}
              name={name}
              onChange={onChangeRevised}
              InputProps={{
                inputProps: {
                  ...rest,
                  maxLength: ZIPCODE_LENGTH,
                  'data-testid': ADDRESS_FORM_DATA_TEST_IDS.zipCodeInput,
                },
              }}
            />
          );
        }}
      />
      <Controller
        name="cityState"
        control={control}
        disabled={disabled}
        render={({ field }) => {
          const { ref, onChange, value, ...rest } = field;
          return (
            <CityStateSelect
              data-testid={ADDRESS_FORM_DATA_TEST_IDS.cityStateSelect}
              cities={cities}
              onChange={onChange}
              value={value || ''}
              {...rest}
            />
          );
        }}
      />
    </>
  );
};

ZipCodeCitySelectFields.displayName = 'ZipCodeCitySelectFields';

ZipCodeCitySelectFields.dataModel = {
  cityStateByZipCode,
};
